import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  Grid,
  Text,
} from '@backstage/ui';
import {
  type CheckDefinition,
  type CheckRule,
  type CheckRuleCondition,
  type PulseFactCollectorInfo,
} from '@internal/backstage-plugin-pulse-common';

const isCheckRuleCondition = (
  item: CheckRuleCondition | CheckRule,
): item is CheckRuleCondition => 'factRef' in item;

const collectMatchingFactRefs = (
  rule: CheckRule,
  targetFactRefs: Set<string>,
  seen = new Set<string>(),
): string[] => {
  const results: string[] = [];
  const items = [...(rule.any ?? []), ...(rule.all ?? [])];

  for (const item of items) {
    if (isCheckRuleCondition(item)) {
      if (targetFactRefs.has(item.factRef) && !seen.has(item.factRef)) {
        seen.add(item.factRef);
        results.push(item.factRef);
      }
    } else {
      results.push(...collectMatchingFactRefs(item, targetFactRefs, seen));
    }
  }

  return results;
};

const getIntegrationFactRefs = (
  factCollectors: PulseFactCollectorInfo[],
): Set<string> =>
  new Set(
    factCollectors.map(
      fc => `${fc.integrationId}:default/${fc.factCollectorId}`,
    ),
  );

const filterDependentChecks = (
  checks: CheckDefinition[],
  targetFactRefs: Set<string>,
): Array<{ check: CheckDefinition; matchingFactRefs: string[] }> => {
  const results: Array<{ check: CheckDefinition; matchingFactRefs: string[] }> =
    [];

  for (const check of checks) {
    const matchingFactRefs = collectMatchingFactRefs(
      check.rule,
      targetFactRefs,
    );
    if (matchingFactRefs.length > 0) {
      results.push({ check, matchingFactRefs });
    }
  }

  return results;
};

type DependentChecksGridProps = {
  factCollectors: PulseFactCollectorInfo[];
  checks: CheckDefinition[];
};

export const DependentChecksGrid = ({
  factCollectors,
  checks,
}: DependentChecksGridProps) => {
  const targetFactRefs = getIntegrationFactRefs(factCollectors);
  const dependentChecks = filterDependentChecks(checks, targetFactRefs);

  if (dependentChecks.length === 0) {
    return null;
  }

  return (
    <Flex direction="column" gap="4">
      <Text variant="title-x-small">Dependent Checks</Text>
      <Grid.Root columns={{ initial: '1', lg: '5' }} gap="4">
        {dependentChecks.map(({ check, matchingFactRefs }) => (
          <Grid.Item key={check.id}>
            <Card href={`/pulse/checks/${check.id}`} label="View check details">
              <CardHeader>
                <Text variant="title-x-small" weight="bold">
                  {check.name}
                </Text>
              </CardHeader>
              <CardBody>
                {check.description && (
                  <Text style={{ overflowWrap: 'anywhere' }}>
                    {check.description}
                  </Text>
                )}
              </CardBody>
              <CardFooter>
                {matchingFactRefs.map(factRef => (
                  <Text
                    key={factRef}
                    variant="body-small"
                    color="secondary"
                    style={{
                      fontFamily: 'monospace',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {factRef}
                  </Text>
                ))}
              </CardFooter>
            </Card>
          </Grid.Item>
        ))}
      </Grid.Root>
    </Flex>
  );
};
